import React, { useState, useEffect } from 'react';
import {
  Shield, Save, RotateCcw, AlertTriangle, TrendingUp, Newspaper,
  Zap, Flag, Clock, Ban, RefreshCw, ChevronDown, ChevronUp,
  Activity, Info, CheckCircle2, XCircle, Loader2,
} from 'lucide-react';
import { riskGuardAPI } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(n) {
  return parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const fieldCls =
  'px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg ' +
  'text-xs focus:border-[var(--accent-primary)] focus:outline-none ' +
  'text-[var(--text-primary)] placeholder-[var(--text-tertiary)] w-full transition-colors';

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ active, onChange, disabled = false }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : () => onChange(!active)}
      className="relative rounded-full transition-all duration-300 shrink-0"
      style={{
        width: '2.25rem', height: '1.25rem',
        backgroundColor: active ? 'var(--accent-primary)' : 'var(--bg-tertiary)',
        border: `1px solid ${active ? 'var(--accent-primary)' : 'var(--border-color)'}`,
        boxShadow: active ? '0 0 8px color-mix(in srgb, var(--accent-primary) 35%, transparent)' : 'none',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="absolute rounded-full bg-white shadow transition-all duration-300"
        style={{
          width: '0.875rem', height: '0.875rem',
          top: '50%', left: active ? 'calc(100% - 3px)' : '3px',
          transform: active ? 'translate(-100%, -50%)' : 'translate(0, -50%)',
        }}
      />
    </button>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────
function Section({ icon: Icon, title, subtitle, color = 'var(--accent-primary)', enabled, onToggle, children, badge }) {
  const [open, setOpen] = useState(true);
  return (
    <div className={`rounded-xl border overflow-hidden transition-all duration-200 ${enabled ? '' : 'opacity-70'}`}
      style={{ backgroundColor: 'var(--bg-secondary)', borderColor: enabled ? color : 'var(--border-color)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}
        style={{ borderBottom: open && enabled ? `1px solid var(--border-color)` : 'none' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
            {badge && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: `color-mix(in srgb, ${color} 15%, transparent)`, color }}>
                {badge}
              </span>
            )}
          </div>
          <p className="text-xs text-[var(--text-tertiary)]">{subtitle}</p>
        </div>
        <Toggle active={enabled} onChange={onToggle} />
        <button className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>
      {open && enabled && (
        <div className="px-4 py-3 space-y-3">{children}</div>
      )}
    </div>
  );
}

// ── Action checkbox ───────────────────────────────────────────────────────────
function ActionCheck({ icon: Icon, label, sublabel, checked, onChange, color = 'var(--accent-primary)', extra }) {
  return (
    <div
      className="flex items-start gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all"
      style={{
        backgroundColor: checked ? `color-mix(in srgb, ${color} 8%, transparent)` : 'var(--bg-tertiary)',
        border: `1px solid ${checked ? `color-mix(in srgb, ${color} 30%, transparent)` : 'var(--border-color)'}`,
      }}
      onClick={() => onChange(!checked)}
    >
      <div className="w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 transition-all"
        style={{
          backgroundColor: checked ? color : 'var(--bg-secondary)',
          border: `1.5px solid ${checked ? color : 'var(--border-color)'}`,
        }}>
        {checked && <Icon className="w-3.5 h-3.5 text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-[var(--text-primary)]">{label}</p>
        {sublabel && <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{sublabel}</p>}
        {extra && checked && <div className="mt-2">{extra}</div>}
      </div>
    </div>
  );
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ label, val, color }) {
  return (
    <div className="px-3 py-2 rounded-lg border" style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)' }}>
      <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-0.5">{label}</p>
      <p className="text-sm font-bold" style={{ color }}>{val}</p>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN
// ═════════════════════════════════════════════════════════════════════════════
export default function RiskGuardConfig() {
  const { t } = useLocale();

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);
  const [success,  setSuccess]  = useState(false);
  const [stats,    setStats]    = useState(null);

  const [cfg, setCfg] = useState({
    // daily profit
    daily_profit_enabled:       false,
    daily_profit_limit:         '',
    // exposure
    exposure_enabled:           false,
    exposure_multiplier:        '',
    // news block
    news_block_enabled:         false,
    news_block_minutes_before:  '',
    news_block_minutes_after:   '',
    // actions
    action_hard_reject:  false,
    action_requote:      false,
    action_delay:        false,
    action_delay_seconds: 3,
    action_flag:         true,
  });

  // ── load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [cfgRes, statsRes] = await Promise.allSettled([
          riskGuardAPI.getConfig(),
          riskGuardAPI.getStats(),
        ]);
        if (cfgRes.status === 'fulfilled' && cfgRes.value?.config) {
          const c = cfgRes.value.config;
          setCfg({
            daily_profit_enabled:      !!c.daily_profit_enabled,
            daily_profit_limit:        c.daily_profit_limit ?? '',
            exposure_enabled:          !!c.exposure_enabled,
            exposure_multiplier:       c.exposure_multiplier ?? '',
            news_block_enabled:        !!c.news_block_enabled,
            news_block_minutes_before: c.news_block_minutes_before ?? '',
            news_block_minutes_after:  c.news_block_minutes_after ?? '',
            action_hard_reject:  !!c.action_hard_reject,
            action_requote:      !!c.action_requote,
            action_delay:        !!c.action_delay,
            action_delay_seconds: c.action_delay_seconds ?? 3,
            action_flag:         c.action_flag !== undefined ? !!c.action_flag : true,
          });
        }
        if (statsRes.status === 'fulfilled') setStats(statsRes.value?.stats || null);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setCfg(f => ({ ...f, [k]: v }));

  // ── save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true); setError(null); setSuccess(false);
    try {
      await riskGuardAPI.saveConfig(cfg);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center py-10 gap-2">
      <Loader2 className="w-5 h-5 animate-spin text-[var(--accent-primary)]" />
      <span className="text-xs text-[var(--text-tertiary)]">Loading Risk Guard config…</span>
    </div>
  );

  const hasAnyRule = cfg.daily_profit_enabled || cfg.exposure_enabled || cfg.news_block_enabled;
  const hasAnyAction = cfg.action_hard_reject || cfg.action_requote || cfg.action_delay || cfg.action_flag;

  return (
    <div className="space-y-4">

      {/* ── Stats bar ── */}
      {stats && (
        <div className="grid grid-cols-4 gap-2">
          <StatPill label="Total Events"    val={stats.total_events   ?? '—'} color="var(--text-primary)" />
          <StatPill label="Hard Rejects"    val={stats.hard_rejects   ?? '—'} color="var(--accent-danger)" />
          <StatPill label="Delayed"         val={stats.delayed        ?? '—'} color="var(--accent-warning)" />
          <StatPill label="Flagged Today"   val={stats.flagged_today  ?? '—'} color="var(--accent-info)" />
        </div>
      )}

      {/* ── Error / Success ── */}
      {error && (
        <div className="flex items-center gap-2 text-xs text-[var(--accent-danger)] bg-[var(--accent-danger)]/10 px-3 py-2 rounded-lg border border-[var(--accent-danger)]/20">
          <XCircle className="w-3.5 h-3.5 shrink-0" />
          <span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><XCircle className="w-3 h-3" /></button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 text-xs text-[var(--accent-success)] bg-[var(--accent-success)]/10 px-3 py-2 rounded-lg border border-[var(--accent-success)]/20">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          <span>Risk Guard configuration saved.</span>
        </div>
      )}

      {/* ────────────────── RULES ────────────────── */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] mb-2 px-0.5">Rules</p>
        <div className="space-y-2">

          {/* Daily Profit Cap */}
          <Section
            icon={TrendingUp}
            title="Daily Profit Cap"
            subtitle="Block / act once account profit hits the daily limit"
            color="var(--accent-danger)"
            enabled={cfg.daily_profit_enabled}
            onToggle={v => set('daily_profit_enabled', v)}
            badge="Account-level"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                Max daily profit (USD)
              </label>
              <input
                className={fieldCls}
                type="number" min="1" step="0.01"
                placeholder="e.g. 5000.00"
                value={cfg.daily_profit_limit}
                onChange={e => set('daily_profit_limit', e.target.value)}
              />
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
                When an account's realised + unrealised P&L exceeds this in one trading day, the configured action fires on new orders.
              </p>
            </div>
          </Section>

          {/* Exposure multiplier */}
          <Section
            icon={Activity}
            title="Notional Exposure Limit"
            subtitle="Cap total open notional vs account balance"
            color="var(--accent-warning)"
            enabled={cfg.exposure_enabled}
            onToggle={v => set('exposure_enabled', v)}
            badge="Per-order"
          >
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                Max exposure multiplier (× balance)
              </label>
              <input
                className={fieldCls}
                type="number" min="0.1" step="0.1"
                placeholder="e.g. 10 (= 10× balance)"
                value={cfg.exposure_multiplier}
                onChange={e => set('exposure_multiplier', e.target.value)}
              />
              <p className="text-[10px] text-[var(--text-tertiary)] mt-1">
                If an incoming order would push total notional above <strong>(balance × multiplier)</strong>, the action fires.
              </p>
            </div>
          </Section>

          {/* News block */}
          <Section
            icon={Newspaper}
            title="News Blackout Window"
            subtitle="Block trading around high-impact economic events"
            color="var(--accent-info)"
            enabled={cfg.news_block_enabled}
            onToggle={v => set('news_block_enabled', v)}
            badge="Symbol-level"
          >
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                  Minutes before event
                </label>
                <input
                  className={fieldCls}
                  type="number" min="1" max="60" step="1"
                  placeholder="e.g. 5"
                  value={cfg.news_block_minutes_before}
                  onChange={e => set('news_block_minutes_before', e.target.value)}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1 block">
                  Minutes after event
                </label>
                <input
                  className={fieldCls}
                  type="number" min="1" max="60" step="1"
                  placeholder="e.g. 5"
                  value={cfg.news_block_minutes_after}
                  onChange={e => set('news_block_minutes_after', e.target.value)}
                />
              </div>
            </div>
            <p className="text-[10px] text-[var(--text-tertiary)]">
              Affected symbols are identified automatically from your enabled instruments feed.
            </p>
          </Section>

        </div>
      </div>

      {/* ────────────────── ACTIONS ────────────────── */}
      <div>
        <p className="text-[10px] uppercase tracking-widest font-bold text-[var(--text-tertiary)] mb-2 px-0.5">
          Actions <span className="normal-case font-normal">(select one or more)</span>
        </p>
        <div className="space-y-2">

          <ActionCheck
            icon={Ban}
            label="Hard Reject"
            sublabel="Order is immediately rejected with a reason code sent to the client."
            checked={cfg.action_hard_reject}
            onChange={v => set('action_hard_reject', v)}
            color="var(--accent-danger)"
          />

          <ActionCheck
            icon={RefreshCw}
            label="Requote"
            sublabel="Order is held briefly and re-offered at a less favourable price."
            checked={cfg.action_requote}
            onChange={v => set('action_requote', v)}
            color="var(--accent-warning)"
          />

          <ActionCheck
            icon={Clock}
            label="Delay Execution"
            sublabel="Introduce a pause before the order hits the book."
            checked={cfg.action_delay}
            onChange={v => set('action_delay', v)}
            color="var(--accent-info)"
            extra={
              <div className="flex items-center gap-2">
                <input
                  className={fieldCls}
                  type="number" min="1" max="30" step="1"
                  value={cfg.action_delay_seconds}
                  onChange={e => set('action_delay_seconds', parseInt(e.target.value) || 3)}
                  style={{ width: '5rem' }}
                />
                <span className="text-xs text-[var(--text-tertiary)]">seconds</span>
              </div>
            }
          />

          <ActionCheck
            icon={Flag}
            label="Flag & Log"
            sublabel="Always write to the risk events log. Recommended — does not affect execution."
            checked={cfg.action_flag}
            onChange={v => set('action_flag', v)}
            color="var(--accent-success)"
          />

        </div>
      </div>

      {/* ────────────────── Validation hints ────────────────── */}
      {hasAnyRule && !hasAnyAction && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-[var(--accent-warning)]/8 border border-[var(--accent-warning)]/20">
          <AlertTriangle className="w-3.5 h-3.5 text-[var(--accent-warning)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--text-secondary)]">
            You have rules enabled but no action selected. At least one action is required.
          </p>
        </div>
      )}

      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/15">
        <Info className="w-3.5 h-3.5 text-[var(--accent-primary)] shrink-0 mt-0.5" />
        <p className="text-[10px] text-[var(--text-secondary)] leading-relaxed">
          Multiple actions can be combined. For example: <strong>Delay + Flag</strong> will hold the order and log the event without rejecting.
          Rules are evaluated in order: Daily Profit → Exposure → News.
        </p>
      </div>

      {/* ────────────────── Save ────────────────── */}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving || (hasAnyRule && !hasAnyAction)}
          className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-all disabled:opacity-40"
          style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : 'Save Risk Guard Config'}
        </button>
      </div>

    </div>
  );
}